import type { Cookies as SvelteCookies } from '@sveltejs/kit';
import {
    Cookies,
    fromDocument,
    fromSvelteCookies,
    type CookieOpts
} from '$lib/services/helpers/cookies';
import { COOKIE_NAMES } from '$lib/services/cookies/constants';
import type { TMarketSlug } from '$lib/services/market/types';
import { MARKET_SLUGS } from '$lib/services/market/slugs';
import type { TMFELocationOverrides, TMFEName } from '$lib/services/blocks/types';

const setCookieValue = (cookieName: string) => (value: string, options?: CookieOpts) => {
    const requestCookies = new Cookies(fromDocument(document));
    requestCookies.set(cookieName, value, options);
};

const getCookieValue = (
    cookieName: string,
    defaultValue: string = '',
    cookies?: SvelteCookies
): string => {
    const cookiesObject = cookies ? fromSvelteCookies(cookies) : fromDocument(document);
    const requestCookies = new Cookies(cookiesObject);
    return requestCookies.get(cookieName) ?? defaultValue;
};

const checkIfCookieIsTrue =
    (cookieName: string) =>
    (cookies?: SvelteCookies): boolean =>
        getCookieValue(cookieName, '', cookies) === 'true';

const toggleValue = (cookieName: string) => (onSet: (value: string) => void) => {
    const previousValue = checkIfCookieIsTrue(cookieName)();
    const newValue = previousValue ? 'false' : 'true';
    setCookieValue(cookieName)(newValue);
    onSet(newValue);
};

export const getMFELocationOverrides = (cookies?: SvelteCookies) => {
    const value = getCookieValue(COOKIE_NAMES.MFE_LOCATION_OVERRIDES, undefined, cookies);

    if (value && shouldBypassCache(cookies)) {
        try {
            return JSON.parse(value) as TMFELocationOverrides;
        } catch (e) {
            return undefined;
        }
    }

    return undefined;
};

const setMFEOverrides = setCookieValue(COOKIE_NAMES.MFE_LOCATION_OVERRIDES);

export const setMFELocationOverrideCookie = (
    mfe: TMFEName,
    value: string,
    onSet: (value: string) => void
) => {
    const mfeLocationOverrides = getMFELocationOverrides() || ({} as Record<TMFEName, string>);

    if (!value) {
        delete mfeLocationOverrides[mfe];
    } else {
        mfeLocationOverrides[mfe] = value;
    }
    setMFEOverrides(JSON.stringify(mfeLocationOverrides));
    onSet(value);
};

export const clearAllMFELocationOverrideCookie = () => setMFEOverrides(JSON.stringify({}));

export const shouldBypassCache = checkIfCookieIsTrue(COOKIE_NAMES.BYPASS_CACHE);
export const toggleBypassCache = toggleValue(COOKIE_NAMES.BYPASS_CACHE);

export const toggleDebugMode = toggleValue(COOKIE_NAMES.DEBUGGING_INFO);
export const shouldShowDebuggingInfo = checkIfCookieIsTrue(COOKIE_NAMES.DEBUGGING_INFO);

export const shouldBypassLISRedirection = checkIfCookieIsTrue(COOKIE_NAMES.BYPASS_LIS_REDIRECTION);
export const toggleBypassLisRedirection = toggleValue(COOKIE_NAMES.BYPASS_LIS_REDIRECTION);

export const toggleProductionEnvMode = toggleValue(COOKIE_NAMES.ENFORCE_PRODUCTION_ENV);

const lobbiesEnabledMarkets = new Set<TMarketSlug>([
    MARKET_SLUGS.EN_CA,
    MARKET_SLUGS.ON,
    MARKET_SLUGS.EN,
    MARKET_SLUGS.ROW,
    MARKET_SLUGS.IE,
    MARKET_SLUGS.EN_NZ,
    MARKET_SLUGS.EN_GB,
    MARKET_SLUGS.FI,
    MARKET_SLUGS.SV,
    MARKET_SLUGS.ES,
    MARKET_SLUGS.NO
]);
export const toggleFabricLobbies = toggleValue(COOKIE_NAMES.SHOW_FABRIC_LOBBIES);
export const shouldShowFabricLobbies = (cookies: SvelteCookies, marketSlug: TMarketSlug): boolean =>
    lobbiesEnabledMarkets.has(marketSlug) ||
    checkIfCookieIsTrue(COOKIE_NAMES.SHOW_FABRIC_LOBBIES)(cookies);

export const setCFSpineOverride = setCookieValue(COOKIE_NAMES.CF_SPINE_OVERRIDE);
